import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
  Link as MuiLink,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { ArrowRight, CalendarDays, CheckCircle2, Link2 } from "lucide-react";
import { StepProps, StepRef } from "../../types";
import { StepHeader } from "../../components/StepHeader";
import { NextStepButton } from "../../components/NextStepButton";
import CalendarFlow from "components/calendar/integrations/ImportCalendarFlow";
import {
  AppleImporter,
  GenericURLImporter,
  OutlookImporter,
  type ImportCalendar,
} from "components/calendar/integrations/imports";
import {
  useCreateCalendarIntegration,
  useGetMemberCalendarIntegrations,
  useUpdateCalendarIntegration,
} from "services/calendar";
import { useGetMemberDetails, useUpdateFamily } from "services/member";
import useIsMobile from "components/hooks/useIsMobile";
import DefaultCalendarSelectionModal from "components/calendar/integrations/DefaultCalendarSelectionModal";
import { CalendarIntegration } from "protogen/calendar_service_pb";
import { UpdateFamilyRequest } from "protogen/advisors_service_pb";

const GOOGLE_REDIRECT_URI = `https://${window.location.host}/get-started/calendar`;

const buildGoogleAuthUrl = () => {
  const clientId = process.env.REACT_APP_GOOGLE_CAL_CLIENT_ID!;
  const returnTo = `/get-started/calendar`;
  const state = encodeURIComponent(JSON.stringify({ returnTo }));
  const scope = encodeURIComponent(
    "email https://www.googleapis.com/auth/calendar",
  );
  return `https://accounts.google.com/o/oauth2/v2/auth?scope=${scope}&access_type=offline&response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(
    GOOGLE_REDIRECT_URI,
  )}&prompt=consent&state=${state}`;
};

const OTHER_CALENDARS: ImportCalendar[] = [
  AppleImporter,
  OutlookImporter,
  GenericURLImporter,
];

export default forwardRef<StepRef, StepProps>(({ onNext, disabled }, _ref) => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [selectedImportType, setSelectedImportType] =
    useState<ImportCalendar | null>(null);
  const [connected, setConnected] = useState(false);
  const [callbackState, setCallbackState] = useState<
    "idle" | "loading" | "success"
  >("idle");
  const [error, setError] = useState("");
  const [defaultPromptIntegration, setDefaultPromptIntegration] =
    useState<CalendarIntegration | null>(null);
  const [defaultPromptFamily, setDefaultPromptFamily] = useState<{
    ref: string;
    defaultCalendarIntegrationRef: string;
    defaultCalendarId: string;
  } | null>(null);
  const hasHandledGoogleCallback = useRef(false);
  const { request: createRequest } = useCreateCalendarIntegration();
  const { request: updateRequest } = useUpdateCalendarIntegration();
  const { request: getIntegrations } = useGetMemberCalendarIntegrations();
  const { request: getMemberDetails } = useGetMemberDetails();
  const { request: updateFamilyRequest, loading: updateFamilyLoading } =
    useUpdateFamily();
  const isWorking = callbackState !== "idle" || updateFamilyLoading;

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const closeDefaultCalendarPrompt = () => {
    setDefaultPromptIntegration(null);
    setDefaultPromptFamily(null);
    onNext?.();
  };

  useEffect(() => {
    const code = searchParams.get("code");
    const scope = searchParams.get("scope");
    if (!code || !scope || hasHandledGoogleCallback.current) {
      return;
    }

    hasHandledGoogleCallback.current = true;

    const requiredScope = "https://www.googleapis.com/auth/calendar";
    if (!scope.split(" ").includes(requiredScope)) {
      const nextParams = new URLSearchParams(location.search);
      nextParams.delete("code");
      nextParams.delete("scope");
      nextParams.delete("state");
      const nextSearch = nextParams.toString();
      window.history.replaceState(
        null,
        "",
        `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`,
      );
      setError(
        'Calendar access was not granted. Please click "Connect Google Calendar" again and make sure to check the box to allow Faye to see and edit your calendar.',
      );
      return;
    }

    setCallbackState("loading");
    setError("");

    const createIntegration = async () => {
      const encodedState = searchParams.get("state");
      let stateData: { replaceIntegrationRef?: string } = {};
      if (encodedState) {
        try {
          stateData = JSON.parse(encodedState);
        } catch (_error) {}
      }

      const createResp = await createRequest({
        calendarType: "google",
        accessCode: code,
        accessScope: scope || "",
        accessRedirectUri: GOOGLE_REDIRECT_URI,
        calendarUrl: "",
        familyRef: "",
      });

      if (!createResp) {
        setCallbackState("idle");
        setError("We couldn't connect your calendar. Please try again.");
        return;
      }

      if (stateData.replaceIntegrationRef) {
        await updateRequest({
          integrationRef: stateData.replaceIntegrationRef,
          deactivate: true,
          activate: false,
          enableCalendarId: "",
          disableCalendarId: "",
        });
      }

      const nextParams = new URLSearchParams(location.search);
      nextParams.delete("code");
      nextParams.delete("scope");
      nextParams.delete("state");
      const nextSearch = nextParams.toString();
      window.history.replaceState(
        null,
        "",
        `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`,
      );

      setConnected(true);
      setCallbackState("success");

      if (createResp.integration?.accessScope === "read_write") {
        const [freshMemberDetails, freshIntegrations] = await Promise.all([
          getMemberDetails(),
          getIntegrations(),
        ]);
        const family = freshMemberDetails?.family;
        const integration =
          freshIntegrations?.importIntegrations.find(
            (i) => i.ref === createResp.integration?.ref,
          ) || createResp.integration;

        if (family && integration) {
          setDefaultPromptFamily({
            ref: family.ref,
            defaultCalendarIntegrationRef: family.defaultCalendarIntegrationRef,
            defaultCalendarId: family.defaultCalendarId,
          });
          setDefaultPromptIntegration(integration);
          return;
        }
      }

      window.setTimeout(() => {
        onNext?.();
      }, 1000);
    };

    createIntegration();
  }, [
    createRequest,
    getIntegrations,
    getMemberDetails,
    location.pathname,
    location.search,
    onNext,
    searchParams,
    updateRequest,
  ]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: isMobile ? "0 16px 32px" : "0 32px",
          gap: isMobile ? "16px" : "24px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "640px",
            display: "flex",
            flexDirection: "column",
            gap: isMobile ? "16px" : "24px",
          }}
        >
          <StepHeader
            title="Add your calendar"
            subtitle={
              isMobile ? (
                <Typography variant="body">
                  Connect your calendar so your bot can create events, spot
                  conflicts, and coordinate your household.
                </Typography>
              ) : (
                <>
                  <Typography variant="body">
                    Households run on Faye via their calendars. Once your
                    calendar is connected, your bot can create events, spot
                    conflicts, suggest timing, prepare reminders, and help
                    coordinate with your partner.
                  </Typography>
                  <Typography variant="body">
                    You can skip this for now, but connecting it early will make
                    your experience magical!
                  </Typography>
                </>
              )
            }
            alignment="left"
          />

          {callbackState === "loading" && (
            <Alert
              icon={<CircularProgress size={18} />}
              severity="info"
              sx={{ borderRadius: "12px" }}
            >
              Connecting your calendar to Faye.
            </Alert>
          )}

          {callbackState === "success" && (
            <Alert
              icon={<CheckCircle2 size={18} />}
              severity="success"
              sx={{ borderRadius: "12px" }}
            >
              Calendar connected. Faye can now plan around your household's
              schedule. Taking you to the next step.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ borderRadius: "12px" }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              border: "1px solid #D4D4D4",
              borderRadius: "16px",
              padding: isMobile ? "16px" : "20px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "6px",
              }}
            >
              <Box>
                <Typography variant="h4">Google Calendar</Typography>
              </Box>
              <Chip
                label="Recommended"
                sx={{ backgroundColor: "#E8F5F2", color: "#198282" }}
              />
            </Box>
            <Button
              variant="contained"
              color="primary"
              href={buildGoogleAuthUrl()}
              endIcon={<ArrowRight size={18} />}
              disabled={isWorking}
            >
              Connect Google Calendar
            </Button>
            <MuiLink
              href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{
                color: "#6B6E7B",
                fontSize: "12px",
                alignSelf: "flex-end",
                lineHeight: 1.2,
                marginTop: 0,
                marginBottom: "-8px",
                pointerEvents: isWorking ? "none" : "auto",
                opacity: isWorking ? 0.6 : 1,
              }}
            >
              Google API Services User Data Policy
            </MuiLink>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Typography variant="bodyHeavy">
              Or import another calendar
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: "12px",
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              {OTHER_CALENDARS.map((calendar) => (
                <Button
                  key={calendar.name}
                  variant="outlined"
                  startIcon={
                    calendar.name === "Other" ? (
                      <Link2 size={16} />
                    ) : (
                      <CalendarDays size={16} />
                    )
                  }
                  sx={{
                    ...(isMobile
                      ? {
                          width: "100%",
                        }
                      : {}),
                  }}
                  onClick={() => {
                    setError("");
                    setSelectedImportType(calendar);
                  }}
                  disabled={isWorking}
                >
                  {calendar.title}
                </Button>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <NextStepButton
              skippable={true}
              hasValue={connected}
              disabled={disabled || isWorking}
              continueClick={() => onNext?.()}
              buttonText={connected ? "Continue" : "Setup later"}
            />
          </Box>
        </Box>
      </Box>

      {selectedImportType && (
        <CalendarFlow
          open={!!selectedImportType}
          importType={selectedImportType}
          onClose={() => setSelectedImportType(null)}
          onSuccess={() => {
            setConnected(true);
            setSelectedImportType(null);
            setCallbackState("success");
            window.setTimeout(() => {
              onNext?.();
            }, 1000);
          }}
        />
      )}

      {defaultPromptIntegration && defaultPromptFamily && (
        <DefaultCalendarSelectionModal
          open={!!defaultPromptIntegration}
          onClose={closeDefaultCalendarPrompt}
          onSelect={async (integrationRef, calendarId) => {
            await updateFamilyRequest(
              new UpdateFamilyRequest({
                familyRef: defaultPromptFamily.ref,
                shouldUpdateDefaultCalendar: true,
                updatedDefaultCalendarIntegrationRef: integrationRef,
                updatedDefaultCalendarId: calendarId,
              }),
            );
            closeDefaultCalendarPrompt();
          }}
          integrations={[defaultPromptIntegration]}
          currentDefaultIntegrationRef={
            defaultPromptFamily.defaultCalendarIntegrationRef
          }
          currentDefaultCalendarId={defaultPromptFamily.defaultCalendarId}
        />
      )}
    </>
  );
});
